import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo, memo } from 'react';
import { DateTime } from 'luxon';
import {
  ChevronLeftDateButton,
  ChevronRightDateButton
} from '~/modules/common/components/ChevronDateButtons/ChevronDateButtons';
import { deepPure } from '~/util';

import {
  DEFAULT_PERIOD_SCALE,
  PERIOD_SCALE_ENUM
} from '~/modules/common/charts/timeline';
import {
  getScaleWidth,
  totalWidth
} from '~/modules/common/charts/timeline/calculations';

import useStyles from './useStyles';

export const ChartRangeItem2 = memo(
  ({
    classes: { openButton, ...classesOverrides } = {},
    leftComponentWidth,
    scale = DEFAULT_PERIOD_SCALE,
    scaleItemCount,
    dynamicStyle = {
      itemContainer: {}
    },
    rightComponentWidth = totalWidth,
    rightComponentContainerProps,
    chartStartDate,
    isFixedRange,
    showPeriodNavigation = false,
    onPreviousClick,
    onNextClick,
    disableNavigationButton = false,
    backgroundImageOverride,
    children
  }) => {
    const classes = useStyles({ classes: classesOverrides });
    const components = React.Children.toArray(children);
    const leftComponent = components[0];
    const timeItems = components[1];
    const rightComponent = components[2];

    const rangeWidth = scaleItemCount * getScaleWidth(scale);
    const containerMinWidth =
      leftComponentWidth +
      rangeWidth +
      (rightComponent ? rightComponentWidth : 0);

    const minWidthStyle = useMemo(
      () => ({
        minWidth: containerMinWidth
      }),
      [containerMinWidth]
    );

    const itemContainerStyle = useMemo(
      () => ({
        ...{
          flexBasis: leftComponentWidth,
          width: leftComponentWidth
        },
        ...dynamicStyle.itemContainer
      }),
      [dynamicStyle.itemContainer, leftComponentWidth]
    );

    const rangeWidthStyle = useMemo(
      () => ({
        width: rangeWidth,
        ...(backgroundImageOverride && {
          backgroundImage: `url(${backgroundImageOverride})`
        })
      }),
      [rangeWidth, backgroundImageOverride]
    );

    return (
      <div className={classes.root} style={minWidthStyle} role="row">
        <div className={classes.itemContainer} style={itemContainerStyle}>
          {leftComponent}
        </div>
        {showPeriodNavigation ? (
          <ChevronLeftDateButton
            className={classes.periodNavigationButton}
            onClick={onPreviousClick}
            disabled={disableNavigationButton}
          />
        ) : null}
        <div
          className={classNames(classes.timeContainer, {
            ...(!backgroundImageOverride && {
              [classes.timeContainerYear]: scale === PERIOD_SCALE_ENUM.YEARS,
              [classes.timeContainerQuarter]:
                scale === PERIOD_SCALE_ENUM.QUARTERS,
              [classes.timeContainerMonth]: scale === PERIOD_SCALE_ENUM.MONTHS,
              [classes.timeContainerWeek]: scale === PERIOD_SCALE_ENUM.WEEKS,
              [classes.timeContainerDay]: scale === PERIOD_SCALE_ENUM.DAYS,
              [classes[`timeContainerDay${chartStartDate.weekday}`]]:
                scale === PERIOD_SCALE_ENUM.DAYS
            }),
            [classes.timeContainerFixedRange]: isFixedRange,
            [classes.navigationOffSet]: showPeriodNavigation
          })}
          style={rangeWidthStyle}
        >
          {timeItems}
        </div>
        {showPeriodNavigation ? (
          <ChevronRightDateButton
            className={classes.periodNavigationButton}
            onClick={onNextClick}
            disabled={disableNavigationButton}
          />
        ) : null}

        {!rightComponent ? null : (
          <div
            className={classes.totalContainer}
            role="gridcell"
            {...rightComponentContainerProps}
          >
            {rightComponent}
          </div>
        )}
      </div>
    );
  }
);

ChartRangeItem2.propTypes = {
  classes: PropTypes.object,
  scale: PropTypes.string.isRequired,
  dynamicStyle: PropTypes.object,
  scaleItemCount: PropTypes.number.isRequired,
  leftComponentWidth: PropTypes.number,
  rightComponentWidth: PropTypes.number,
  children: PropTypes.node,
  chartStartDate: PropTypes.instanceOf(DateTime).isRequired,
  isFixedRange: PropTypes.bool,
  showPeriodNavigation: PropTypes.bool,
  onPreviousClick: PropTypes.func,
  onNextClick: PropTypes.func,
  disableNavigationButton: PropTypes.bool,
  rightComponentContainerProps: PropTypes.object,
  backgroundImageOverride: PropTypes.string
};

export default deepPure(ChartRangeItem2);
